/**
 * Pending pull requests board (design §10) — project-detail section listing
 * `open`/`reviewing` PRs across the project's repository resources.
 *
 * - Section renders only when the project has ≥1 `type:'repository'` resource
 *   (§5.1, G3) — otherwise it returns null.
 * - Data: per repository `GET /api/repositories/:repoId/pull-requests`, client
 *   filter `status ∈ {open, reviewing}` (audit #5). The APIs are repo-scoped,
 *   the board is project-scoped, so the number of queries varies with the
 *   resources; each repository's subscription is owned by a fetcher child
 *   (hooks are legal there — one instance per row of the fixed repo list) and
 *   lifted into the shared table state. The job column joins the shared
 *   coding-jobs cache (same query args as `CodingJobsList` — one fetch).
 * - Display + link-out only: P5 adds zero client-side mutations (P5-3).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ExternalLink, Inbox } from 'lucide-react';

import { EmptyState } from '@/components/shared/EmptyState';
import { DataTable, type DataTableColumn } from '@/components/shared/DataTable';
import { LoadingRows } from '@/components/shared/LoadingRows';
import { QueryErrorView } from '@/components/common/QueryErrorView';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useGetResourcesQuery } from '@/features/projects/api/projectsApi';

import {
  useGetCodingJobsQuery,
  useGetPullRequestsQuery,
  useGetRepositoryQuery,
} from '../api/coderApi';
import type { CodingJobItemView, PullRequestView, RepositoryView } from '../types';
import { derivePullRequestUrl, docId, providerLabel } from '../lib';
import { JobStatusBadge } from './JobStatusBadge';

export interface PendingPullRequestsBoardProps {
  projectId: string;
}

interface RepoPrState {
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  prs: PullRequestView[];
  repo?: RepositoryView;
  refetch: () => void;
}

interface PendingPrRow {
  pr: PullRequestView;
  repo?: RepositoryView;
  jobItem?: CodingJobItemView;
}

const EMPTY_PRS: PullRequestView[] = [];

const PR_STATUS_LABEL: Record<'open' | 'reviewing', string> = {
  open: 'Open',
  reviewing: 'Reviewing',
};

/** One repository's PR + repo subscription; renders nothing, lifts state up. */
function RepoPullRequestsFetcher({
  repoId,
  onState,
}: {
  repoId: string;
  onState: (repoId: string, state: RepoPrState) => void;
}) {
  const repoQuery = useGetRepositoryQuery(repoId);
  const prsQuery = useGetPullRequestsQuery(repoId);

  useEffect(() => {
    onState(repoId, {
      isLoading: prsQuery.isLoading || repoQuery.isLoading,
      isError: prsQuery.isError,
      error: prsQuery.error,
      prs: prsQuery.data ?? EMPTY_PRS,
      repo: repoQuery.data,
      refetch: () => {
        void prsQuery.refetch();
      },
    });
    // `refetch` identity churn is irrelevant here: the parent's merge ignores
    // it (see handleRepoState); the data/flag deps are the substantive inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    repoId,
    prsQuery.isLoading,
    prsQuery.isError,
    prsQuery.error,
    prsQuery.data,
    repoQuery.isLoading,
    repoQuery.data,
  ]);

  return null;
}

export function PendingPullRequestsBoard({ projectId }: PendingPullRequestsBoardProps) {
  const resourcesQuery = useGetResourcesQuery({ projectId, type: 'repository' });
  const repoIds = useMemo(
    () => (resourcesQuery.data ?? []).map((resource) => resource.refId).filter(Boolean),
    [resourcesQuery.data],
  );
  const hasRepositories = repoIds.length > 0;

  const [repoStates, setRepoStates] = useState<Record<string, RepoPrState>>({});
  const handleRepoState = useCallback((repoId: string, state: RepoPrState) => {
    setRepoStates((prev) => {
      const current = prev[repoId];
      if (
        current &&
        current.isLoading === state.isLoading &&
        current.isError === state.isError &&
        current.error === state.error &&
        current.prs === state.prs &&
        current.repo === state.repo
      ) {
        // Keep the freshest refetch closure without re-rendering.
        return { ...prev, [repoId]: { ...current, refetch: state.refetch } };
      }
      return { ...prev, [repoId]: state };
    });
  }, []);

  const jobsQuery = useGetCodingJobsQuery(projectId, { skip: !hasRepositories });

  // Job join (design §10.2): first item with matching (repositoryId, prNumber).
  const jobItemsByRepoAndNumber = useMemo(() => {
    const map = new Map<string, CodingJobItemView>();
    for (const job of jobsQuery.data ?? []) {
      for (const item of job.items ?? []) {
        if (item.prNumber !== undefined) {
          const key = `${item.repositoryId}:${item.prNumber}`;
          if (!map.has(key)) {
            map.set(key, item);
          }
        }
      }
    }
    return map;
  }, [jobsQuery.data]);

  const rows = useMemo<PendingPrRow[]>(() => {
    const out: PendingPrRow[] = [];
    for (const repoId of repoIds) {
      const state = repoStates[repoId];
      if (!state) {
        continue;
      }
      for (const pr of state.prs) {
        if (pr.status !== 'open' && pr.status !== 'reviewing') {
          continue; // merged/closed dropped (§10.2)
        }
        out.push({
          pr,
          repo: state.repo,
          jobItem: jobItemsByRepoAndNumber.get(`${pr.repositoryId}:${pr.providerNumber}`),
        });
      }
    }
    return out.sort(
      (a, b) => new Date(b.pr.updatedAt ?? 0).getTime() - new Date(a.pr.updatedAt ?? 0).getTime(),
    );
  }, [repoIds, repoStates, jobItemsByRepoAndNumber]);

  // Section visibility: repo-less projects omit the section entirely (§5.1/G3).
  if (!resourcesQuery.isLoading && !hasRepositories) {
    return null;
  }

  const subscribed = repoIds.map((repoId) => repoStates[repoId]);
  const stillLoading = subscribed.some((state) => !state || state.isLoading);
  const errorState = subscribed.find((state) => state?.isError);

  const columns: DataTableColumn<PendingPrRow>[] = [
    {
      id: 'pull-request',
      header: 'Pull request',
      cell: (row) => {
        const url = derivePullRequestUrl(row.repo, row.pr.providerNumber);
        const label = `Open pull request #${row.pr.providerNumber} on ${providerLabel(row.repo?.provider)}`;
        const title = (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="block max-w-[18rem] truncate">{row.pr.title}</span>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">{row.pr.title}</TooltipContent>
          </Tooltip>
        );
        const number = <span className="shrink-0 tabular-nums">#{row.pr.providerNumber}</span>;

        if (!url) {
          // Underivable → no dead link (B3/F3): number + title, unlinked.
          return (
            <span className="flex min-w-0 items-center gap-2 text-sm">
              {number}
              {title}
            </span>
          );
        }

        return (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={label}
            className="flex min-w-0 items-center gap-1.5 rounded-md text-sm hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            {number}
            {title}
            <ExternalLink
              className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
              aria-hidden="true"
            />
            <span className="sr-only"> (opens in a new tab)</span>
          </a>
        );
      },
    },
    {
      id: 'repository',
      header: 'Repository',
      cell: (row) =>
        row.repo?.name ? (
          <span className="text-sm">{row.repo.name}</span>
        ) : (
          <span className="font-mono text-xs text-muted-foreground">{row.pr.repositoryId}</span>
        ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: (row) => (
        // Text labels, never color-only (B1): exactly the two pending statuses.
        <Badge variant={row.pr.status === 'reviewing' ? 'secondary' : 'outline'}>
          {PR_STATUS_LABEL[row.pr.status === 'reviewing' ? 'reviewing' : 'open']}
        </Badge>
      ),
    },
    {
      id: 'coding-job',
      header: 'Coding job',
      cell: (row) =>
        row.jobItem ? (
          <JobStatusBadge status={row.jobItem.status} />
        ) : (
          // Human-opened PRs are legitimate rows here (§10.2).
          <span className="text-sm text-muted-foreground">—</span>
        ),
    },
  ];

  function retryAll() {
    for (const state of Object.values(repoStates)) {
      state.refetch();
    }
    void resourcesQuery.refetch();
  }

  return (
    <section aria-labelledby="pending-pull-requests-title" className="mt-6">
      <h2 id="pending-pull-requests-title" className="text-lg font-semibold">
        Pending pull requests
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Open and in-review pull requests from this project's repositories.
      </p>

      {repoIds.map((repoId) => (
        <RepoPullRequestsFetcher key={repoId} repoId={repoId} onState={handleRepoState} />
      ))}

      {resourcesQuery.isLoading ? (
        <LoadingRows label="Loading pull requests…" columns={4} className="mt-4" />
      ) : errorState ? (
        <QueryErrorView error={errorState.error} label="pull requests" onRetry={retryAll} />
      ) : stillLoading ? (
        <LoadingRows label="Loading pull requests…" columns={4} className="mt-4" />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Inbox />}
          title="No pending pull requests"
          description="Pull requests opened by your coding agents appear here."
          className="mt-4"
        />
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          pageSize={10}
          getRowId={(row) => docId(row.pr) || `${row.pr.repositoryId}-${row.pr.providerNumber}`}
          className="mt-4"
        />
      )}
    </section>
  );
}
